import * as Yup from "yup";
import {
  ClinicalInfoInterface,
  DemographicInfoInterface,
} from "../../types/PatientData.interface";
import { isValidPhoneNumber } from "./phoneNumber";

export enum StepsEnum {
  DEMOGRAPHICS = 0,
  ADDRESS = 1,
  CLINICAL_ONE = 2,
  CLINICAL_TWO = 3,
  CLINICAL_THREE = 4,
  LAST_STEP = 5,
}

export const phoneNumberValidation = (value: string) => {
  return isValidPhoneNumber(value);
};


// Refactor validationSchemas into a function
const getValidationSchemas = (
  patientData: ClinicalInfoInterface & DemographicInfoInterface
): Record<StepsEnum, Yup.ObjectSchema<any>> => {
  return {
    [StepsEnum.DEMOGRAPHICS]: Yup.object().shape({
      honorific: Yup.mixed().required("Honorific is required."),

      // `dropdownWithText` allows custom values (e.g. "Non-binary"),
      // so `gender` must not be restricted to enum values here.
      gender: Yup.string()
        .trim()
        .required("Gender is required.")
        .max(50, "Gender must be at most 50 characters."),

      firstName: Yup.string()
        .max(20, "First name must be at most 20 characters.")
        .required("First name is required."),

      identityNumber: Yup.string()
        .min(8, "Identity number must be at least 8 characters.")
        .max(15, "Identity number must not exceed 15 characters.")
        .required("Identity number is required."),

      lastName: Yup.string()
        .max(20, "Last name must be at most 20 characters.")
        .required("Last name is required."),

      birthday: Yup.date()
        .required("Birthday is required.")
        .typeError("Invalid date for birthday.")
        .max(new Date(), "Birthday cannot be in the future.")
        .test(
          "is-18-or-older",
          "You must be at least 18 years old.",
          function (value) {
            if (!value) return false;

            const today = new Date();
            const minAgeDate = new Date(
              today.getFullYear() - 18,
              today.getMonth(),
              today.getDate()
            );

            return value <= minAgeDate;
          }
        ),

      phoneNumber: Yup.string()
        .max(20, "Phone number must be at most 20 characters.")
        .required("Phone number is required.")
        .test("is-valid-phone", "Phone number is not valid.", (value) =>
          phoneNumberValidation(value)
        ),

      email: Yup.string()
        .email("Email must be a valid format.")
        .max(50, "Email must be at most 50 characters.")
        .required("Email is required."),

      middleName: Yup.string()
        .max(20, "Middle name must be at most 20 characters.")
        .nullable(),

      // `dropdownWithText` allows custom values.
      maritalStatus: Yup.string()
        .trim()
        .required("Marital Status required")
        .max(50, "Marital Status must be at most 50 characters."),

      // `dropdownWithText` allows custom values.
      race: Yup.string()
        .trim()
        .required("Race is required.")
        .max(50, "Race must be at most 50 characters."),
    }),

    [StepsEnum.ADDRESS]: Yup.object().shape({
      state: Yup.string().when("country", ([country], schema) => {
        return country === "United States"
          ? schema.required("State is required for US.")
          : schema.notRequired();
      }),

      address: Yup.string()
        .max(50, "Address must be at most 50 characters.")
        .required("Address is required."),

      city: Yup.string()
        .max(20, "City must be at most 20 characters.")
        .required("City is required."),

      zip: Yup.string(),

      country: Yup.string().required("Country is required."),
    }),

    [StepsEnum.CLINICAL_ONE]: Yup.object().shape({
      isSymptomatic: Yup.boolean()
        .required("Symptomatic status is required.")
        .test(
          "is-valid-symptomatic",
          "Invalid symptomatic status.",
          function (value) {
            return value === true || value === false;
          }
        ),

      // Validate symptom-related questions only if `isSymptomatic` is `true`
      ...(patientData.isSymptomatic
        ? {
          symptomsType: Yup.mixed()
            .oneOf(
              ["Acute", "Chronic"],
              "Symptoms type must be Acute or Chronic."
            )
            .required("Symptoms type is required."),
          chestPain: Yup.boolean()
            .required("Chest pain status is required.")
            .test(
              "is-valid-chest-pain",
              "Chest pain status is required.",
              function (value) {
                return value === true || value === false;
              }
            ),
          chestPainType: Yup.mixed()
            .oneOf(
              ["Typical", "Atypical"],
              "Chest pain type must be Typical or Atypical."
            )
            .nullable()
            .when("chestPain", {
              is: true,
              then: (schema) =>
                schema.required("Chest pain type is required."),
              otherwise: (schema) => schema.notRequired().nullable(),
            })
            .test(
              "is-valid-chest-pain-type",
              "Chest pain type must be Typical or Atypical.",
              function (value) {
                const chestPain = this.parent.chestPain;
                if (!chestPain) return true;
                return value === "Typical" || value === "Atypical";
              }
            ),
          shortnessOfBreath: Yup.boolean().required(
            "Shortness of breath status is required."
          ),
          shoulderOrArmPain: Yup.boolean().required(
            "Shoulder or arm pain status is required."
          ),
          jawPain: Yup.boolean().required("Jaw pain status is required."),
          dizzinessOrPresyncope: Yup.boolean().required(
            "Dizziness or presyncope status is required."
          ),
          nausea: Yup.boolean().required("Nausea status is required."),
        }
        : {}),
    }),

    [StepsEnum.CLINICAL_TWO]: Yup.object().shape({
      smokingStatus: Yup.mixed()
        .oneOf(
          [
            "No",
            "Cessation Over 5yrs",
            "Cessation Less than 5yrs",
            "Yes (Over a pack/day)",
            "Yes (Less than a pack/day)",
          ],
          "Invalid smoking status."
        )
        .required("Smoking status is required."),

      takingOtherDrugs: Yup.string().required(
        "Taking other drugs field is required."
      ),

      totalCholesterol: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("Total cholesterol must be a number.")
        .positive("Total cholesterol must be a positive number.")
        .min(10, "Total cholesterol must be at least 10 mg/dL.")
        .max(600, "Total cholesterol must not exceed 600 mg/dL.")
        .required("Total cholesterol is required."),

      ldl: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("LDL must be a number.")
        .positive("LDL must be a positive number.")
        .min(10, "LDL must be at least 10 mg/dL.")
        .max(500, "LDL must not exceed 500 mg/dL.")
        .required("LDL is required."),

      triglycerides: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("Triglycerides must be a number.")
        .positive("Triglycerides must be a positive number.")
        .min(10, "Triglycerides must be at least 10 mg/dL.")
        .max(990, "Triglycerides must not exceed 990 mg/dL.")
        .required("Triglycerides are required."),

      bpSystolic: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("BP systolic must be a number.")
        .integer("BP systolic must be an integer.")
        .min(50, "Systolic blood pressure must be at least 50 mmHg.")
        .max(300, "Systolic blood pressure must not exceed 300 mmHg.")
        .required("BP systolic is required."),

      bpDiastolic: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("BP diastolic must be a number.")
        .integer("BP diastolic must be an integer.")
        .required("BP diastolic is required.")
        .min(30, "Diastolic blood pressure must be at least 30 mmHg.")
        .max(200, "Diastolic blood pressure must not exceed 200 mmHg."),


      sugarLevel: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("Sugar level must be a number.")
        .positive("Sugar level must be a positive number.")
        .min(30, "Sugar level must be at least 30 mg/dL.")
        .max(600, "Sugar level must not exceed 600 mg/dL.")
        .required("Sugar level is required."),

      height: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("Height must be a number.")
        .test("height-range", function (value) {

          const { metrics } = this.parent;
          if(metrics === 'imperial'){
            return true;
          }
          if (!value) {
            return this.createError({ message: "Height is required." });
          }
          if (value < 100 || value > 250) {
            return this.createError({
              message: "Height must be between 100 and 250 cm.",
            });
          }
          return true;
        })
        .required("Height is required."),


      // heightInch: Yup.number()
      //   .test("height-range", function (value) {
      //     if (!value) {
      //       return this.createError({ message: "Height is required." });
      //     }
      //     // if (value < 36 || value > 96) {
      //     //   return this.createError({
      //     //     message: "Height must be between 36 and 96 inches.",
      //     //   });
      //     // }
      //     return true;
      //   })
      //   .required("Height is required."),

      heightFeet: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("Height must be a number.")
        .test("height-range", function (value) {
          const { heightInch , metrics } = this.parent;
          if(metrics === 'metric'){
            return true;
          }
          if (!value) {
            return this.createError({ message: "Height is required." });
          }
       
          

          const totalInches = value * 12 + Number(heightInch);

          if (totalInches < 36 || totalInches > 96) {
            return this.createError({
              message: "Height must be between 3 and 8 ft.",
            });
          }
          return true;
        })
        .required("Height is required."),



      weight: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("Weight must be a number.")
        .test("weight-range", function (value) {

          if (!value)
            return this.createError({ message: "Weight is required." });

          if (value < 20 || value > 400) {
            return this.createError({
              message: "Weight must be between 20 and 400 kg.",
            });
          }

          return true;
        })
        .required("Weight is required."),
      weightLbs: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue == null ? undefined : value;
        })
        .typeError("Weight must be a number.")
        .test("weight-range", function (value) {
          if (!value)
            return this.createError({ message: "Weight is required." });

          if (value < 40 || value > 880) {
            return this.createError({
              message: "Weight must be between 40 and 880 lb.",
            });
          }

          return true;
        })
        .required("Weight is required."),
      notes: Yup.string().nullable(),
    }),

    [StepsEnum.CLINICAL_THREE]: Yup.object().shape({
      copd: Yup.boolean().required("COPD status is required."),

      arrhythmiasOrConductionAbnormality: Yup.boolean().required(
        "Arrhythmias or conduction abnormality status is required."
      ),

      peripheralArterialDisease: Yup.boolean().required(
        "Peripheral arterial disease status is required."
      ),

      familyHistoryCardiovascularEvents: Yup.boolean().required(
        "Family history of cardiovascular events status is required."
      ),

      cerebrovascularDisease: Yup.boolean().required(
        "Cerebrovascular disease status is required."
      ),

      strokeOrTiaOrRIND: Yup.boolean().required(
        "Stroke, TIA, or RIND status is required."
      ),

      chronicHeartFailure: Yup.boolean().required(
        "Chronic heart failure status is required."
      ),
    }),

    [StepsEnum.LAST_STEP]: Yup.object().shape({
      hemodynamicInstability: Yup.boolean().required(
        "Hemodynamic instability status is required."
      ),

      previousCardiacEvent: Yup.boolean().required(
        "Previous cardiac event status is required."
      ),
      otherCoMorbidities: Yup.string().nullable(),
      lvefLessThan30: Yup.boolean().required("LVEF <30% status is required."),

      pulmonaryEdema: Yup.boolean().required(
        "Pulmonary edema status is required."
      ),

      anemia: Yup.boolean().required("Anemia status is required."),
    }),
  };
};

export default getValidationSchemas;
